import React, { useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GoogleIcon from '@mui/icons-material/Google';
import AppleIcon from '@mui/icons-material/Apple';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const CalendarSyncPanel = (props) => {
  const [showSyncOptions, setShowSyncOptions] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [activeTab, setActiveTab] = useState('google');

  const handleCopyUrl = () => {
    // In a real implementation, this would copy a unique calendar URL
    navigator.clipboard.writeText(`https://getrightwork.dev/api/calendar?employee=${props.employeeScheduleSyncHash}`);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  return (
    <div className="w-full">
      {/* Main Sync Button */}
      <div className="w-full flex justify-center pt-6 pb-4 border-t border-slate-200">
        <button
          className="flex items-center justify-center px-4 py-2 bg-violet-100 text-violet-700 rounded-lg hover:bg-violet-200 transition-colors"
          onClick={() => setShowSyncOptions(!showSyncOptions)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="mr-2" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          {showSyncOptions ? 'Hide Sync Options' : 'Sync Your Schedule'}
        </button>
      </div>

      {/* Expandable Sync Panel */}
      {showSyncOptions && (
        <div className="bg-white rounded-lg p-4 border border-slate-200 mx-4 mb-6 text-sm">
          <h4 className="text-slate-800 font-medium mb-4">Sync your schedule with your calendar</h4>

          {/* Calendar Selection Tabs */}
          <div className="flex border-b border-slate-200 mb-4">
            <button
              className={`py-2 px-3 flex items-center ${activeTab === 'google' ? 'border-b-2 border-violet-600 text-violet-600' : 'text-slate-600'}`}
              onClick={() => setActiveTab('google')}
            >
              <GoogleIcon style={{ fontSize: "18px", marginRight: "8px" }} />
              Google
            </button>
            <button
              className={`py-2 px-3 flex items-center ${activeTab === 'apple' ? 'border-b-2 border-violet-600 text-violet-600' : 'text-slate-600'}`}
              onClick={() => setActiveTab('apple')}
            >
              <AppleIcon style={{ fontSize: "18px", marginRight: "8px" }} />
              Apple
            </button>
            <button
              className={`py-2 px-3 flex items-center ${activeTab === 'outlook' ? 'border-b-2 border-violet-600 text-violet-600' : 'text-slate-600'}`}
              onClick={() => setActiveTab('outlook')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2">
                <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
              </svg>
              Outlook
            </button>
            <button
              className={`py-2 px-3 flex items-center ${activeTab === 'url' ? 'border-b-2 border-violet-600 text-violet-600' : 'text-slate-600'}`}
              onClick={() => setActiveTab('url')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
              </svg>
              Calendar URL
            </button>
          </div>

          {/* Google Calendar Instructions */}
          {activeTab === 'google' && (
            <div className="space-y-4">
              <p className="text-slate-600">Add your work schedule to Google Calendar in two simple steps:</p>

              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <h5 className="font-medium text-slate-800 mb-2 flex items-center">
                  <span className="inline-flex items-center justify-center bg-violet-100 text-violet-700 w-5 h-5 rounded-full mr-2 text-xs">1</span>
                  Copy your unique calendar URL
                </h5>
                <div className="flex items-center bg-white border border-slate-300 rounded px-3 py-2">
                  <input
                    type="text"
                    value={`https://getrightwork.dev/api/calendar?employee=${props.employeeScheduleSyncHash}`}
                    className="text-slate-600 text-xs flex-1 border-none outline-none"
                    readOnly
                  />
                  <button
                    onClick={handleCopyUrl}
                    className="ml-2 text-violet-600 hover:text-violet-800"
                  >
                    {copiedUrl ? (
                      <div className="flex items-center text-green-600">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                        <span className="ml-1 text-xs">Copied!</span>
                      </div>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <h5 className="font-medium text-slate-800 mb-2 flex items-center">
                  <span className="inline-flex items-center justify-center bg-violet-100 text-violet-700 w-5 h-5 rounded-full mr-2 text-xs">2</span>
                  Add to Google Calendar
                </h5>
                <ol className="space-y-2 text-slate-600 pl-5 list-decimal">
                  <li>Open Google Calendar in your browser</li>
                  <li>Click the "+" next to "Other calendars"</li>
                  <li>Select "From URL"</li>
                  <li>Paste the URL you copied above</li>
                  <li>Click "Add calendar"</li>
                </ol>
                <div className="mt-3">
                  <a
                    href="https://calendar.google.com"
                    target="_blank"
                    className="flex items-center text-sm font-medium text-violet-600 hover:text-violet-800 bg-white px-3 py-2 rounded border border-slate-200 justify-center"
                  >
                    <GoogleIcon style={{ fontSize: "18px", marginRight: "8px" }} />
                    Open Google Calendar
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Apple Calendar Instructions */}
          {activeTab === 'apple' && (
            <div className="space-y-4">
              <p className="text-slate-600">Add your work schedule to Apple Calendar in two simple steps:</p>

              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <h5 className="font-medium text-slate-800 mb-2 flex items-center">
                  <span className="inline-flex items-center justify-center bg-violet-100 text-violet-700 w-5 h-5 rounded-full mr-2 text-xs">1</span>
                  Copy your unique calendar URL
                </h5>
                <div className="flex items-center bg-white border border-slate-300 rounded px-3 py-2">
                  <input
                    type="text"
                    value={`https://getrightwork.dev/api/calendar?employee=${props.employeeScheduleSyncHash}`}
                    className="text-slate-600 text-xs flex-1 border-none outline-none"
                    readOnly
                  />
                  <button
                    onClick={handleCopyUrl}
                    className="ml-2 text-violet-600 hover:text-violet-800"
                  >
                    {copiedUrl ? (
                      <div className="flex items-center text-green-600">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                        <span className="ml-1 text-xs">Copied!</span>
                      </div>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <h5 className="font-medium text-slate-800 mb-2 flex items-center">
                  <span className="inline-flex items-center justify-center bg-violet-100 text-violet-700 w-5 h-5 rounded-full mr-2 text-xs">2</span>
                  Add to Apple Calendar
                </h5>
                <ol className="space-y-2 text-slate-600 pl-5 list-decimal">
                  <li>Open the Calendar app on your device</li>
                  <li>Go to File → New Calendar Subscription (on macOS)</li>
                  <li>On iOS, go to Settings → Accounts → Add Account → Other → Add Subscribed Calendar</li>
                  <li>Paste the URL you copied above</li>
                  <li>Click "Subscribe" and adjust refresh settings if needed</li>
                </ol>
                <div className="mt-3">
                  <button
                    className="flex items-center text-sm font-medium text-violet-600 hover:text-violet-800 bg-white px-3 py-2 rounded border border-slate-200 w-full justify-center"
                  >
                    <AppleIcon style={{ fontSize: "18px", marginRight: "8px" }} />
                    Download Calendar File
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Outlook Calendar Instructions */}
          {activeTab === 'outlook' && (
            <div className="space-y-4">
              <p className="text-slate-600">Add your work schedule to Outlook Calendar in two simple steps:</p>

              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <h5 className="font-medium text-slate-800 mb-2 flex items-center">
                  <span className="inline-flex items-center justify-center bg-violet-100 text-violet-700 w-5 h-5 rounded-full mr-2 text-xs">1</span>
                  Copy your unique calendar URL
                </h5>
                <div className="flex items-center bg-white border border-slate-300 rounded px-3 py-2">
                  <input
                    type="text"
                    value={`https://getrightwork.dev/api/calendar?employee=${props.employeeScheduleSyncHash}`}
                    className="text-slate-600 text-xs flex-1 border-none outline-none"
                    readOnly
                  />
                  <button
                    onClick={handleCopyUrl}
                    className="ml-2 text-violet-600 hover:text-violet-800"
                  >
                    {copiedUrl ? (
                      <div className="flex items-center text-green-600">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                        <span className="ml-1 text-xs">Copied!</span>
                      </div>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <h5 className="font-medium text-slate-800 mb-2 flex items-center">
                  <span className="inline-flex items-center justify-center bg-violet-100 text-violet-700 w-5 h-5 rounded-full mr-2 text-xs">2</span>
                  Add to Outlook Calendar
                </h5>
                <ol className="space-y-2 text-slate-600 pl-5 list-decimal">
                  <li>Open Outlook Calendar</li>
                  <li>Click "Add Calendar" → "From Internet"</li>
                  <li>Paste the URL you copied above</li>
                  <li>Click "OK" and name your calendar</li>
                </ol>
                <div className="mt-3">
                  <a
                    href="https://outlook.office.com/calendar/"
                    target="_blank"
                    className="flex items-center text-sm font-medium text-violet-600 hover:text-violet-800 bg-white px-3 py-2 rounded border border-slate-200 justify-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2">
                      <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                    </svg>
                    Open Outlook Calendar
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Calendar URL tab */}
          {activeTab === 'url' && (
            <div className="space-y-4">
              <p className="text-slate-600">Use this URL to subscribe to your work schedule in any calendar app that supports iCal/ICS subscriptions.</p>

              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <h5 className="font-medium text-slate-800 mb-2">Your Calendar URL</h5>
                <div className="flex items-center bg-white border border-slate-300 rounded px-3 py-2">
                  <input
                    type="text"
                    value={`https://getrightwork.dev/api/calendar?employee=${props.employeeScheduleSyncHash}`}
                    className="text-slate-600 text-xs flex-1 border-none outline-none"
                    readOnly
                  />
                  <button
                    onClick={handleCopyUrl}
                    className="ml-2 text-violet-600 hover:text-violet-800"
                  >
                    {copiedUrl ? (
                      <div className="flex items-center text-green-600">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                        <span className="ml-1 text-xs">Copied!</span>
                      </div>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                    )}
                  </button>
                </div>
                <p className="mt-3 text-slate-500 text-xs">This URL is unique to your schedule. Don't share it with others.</p>
              </div>

              {/* <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <h5 className="font-medium text-slate-800 mb-3">Download Calendar File</h5>
                <button
                  className="flex items-center justify-center w-full bg-white border border-slate-200 rounded px-4 py-2 text-violet-600 hover:bg-violet-50"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  Download .ics File
                </button>
                <p className="mt-3 text-slate-500 text-xs">This file includes your current schedule. For automatic updates, use the calendar URL instead.</p>
              </div> */}
            </div>
          )}

          {/* Sync Settings */}
          {/* <div className="mt-4 pt-4 border-t border-slate-200">
            <h5 className="font-medium text-slate-800 mb-3">Sync Settings</h5>
            <div className="flex items-center justify-between text-sm">
              <label className="text-slate-600">Auto-sync schedule changes</label>
              <div className="relative inline-block w-10 h-5 rounded-full bg-slate-200">
                <input type="checkbox" className="sr-only" defaultChecked />
                <span className="absolute left-1 top-1 bg-white w-3 h-3 rounded-full transform translate-x-5 transition-transform"></span>
              </div>
            </div>
            <div className="flex items-center justify-between mt-3 text-sm">
              <label className="text-slate-600">Include shift notes</label>
              <div className="relative inline-block w-10 h-5 rounded-full bg-slate-200">
                <input type="checkbox" className="sr-only" defaultChecked />
                <span className="absolute left-1 top-1 bg-white w-3 h-3 rounded-full transform translate-x-5 transition-transform"></span>
              </div>
            </div>
          </div> */}

          {/* Help Section */}
          <div className="mt-4 bg-violet-50 p-3 rounded-lg border border-violet-100 flex justify-center">
            <div>
              <div className="flex items-center justify-center space-x-2 text-violet-700">
                <InfoOutlinedIcon style={{ fontSize: "18px", marginBottom: "3px" }} />
                <p className="text-violet-700 text-sm mb-1">Need help?</p>
              </div>
              <p className="text-violet-600 text-xs">Your calendar will update automatically every 24 hours. Having trouble? Contact your manager.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarSyncPanel;
